import { Accordion } from '@zoopla-eng/zds-components/accordion';
import { Box } from '@zoopla-eng/zds-components/box';
import { Button } from '@zoopla-eng/zds-components/button';
import { Divider } from '@zoopla-eng/zds-components/divider';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { Added, AddedToSite } from './AddedToSite';
import { Availability, AvailableFrom } from './Availability';
import { Bedrooms, BedsMax, BedsMin } from './Bedrooms';
import { Features } from './Features';
import {
    accordionAndKeywordsStyle,
    accordionContainerStyle,
    buttonContainerStyle
} from './Filters.styles.css';
import { FurnishedState, Furnishing } from './Furnishing';
import { Keywords } from './Keywords';
import { LetAgreed } from './LetAgreed';
import {
    IsRetirementHome,
    IsSharedAccommodation,
    LettingType
} from './LettingType';
import { BillsIncluded, MustHaves, PetsAllowed } from './MustHaves';
import { Price, PriceFrequency, PriceMax, PriceMin } from './Price';
import { PropertySubType, PropertyType } from './PropertyType';
import { buttonWrapperStyle } from './ToRentFilters.styles.css';
import { trackFilterClick } from './utils/';
import {
    getDefaultToRentFilters,
    getToRentFilters
} from './utils/getToRentFilters';

const getSearchPath = (
    propertyType: PropertyType | 'retirement' | 'bungalow',
    searchPath: string | string[]
) => {
    const searchPaths = {
        property: 'property',
        houses: 'houses',
        flats: 'flats',
        farms_land: 'property',
        retirement: 'retirement',
        bungalow: 'bungalows'
    };

    if (!searchPath || typeof searchPath === 'string') {
        return [searchPaths[propertyType]];
    }

    // there is a bug in LSRP and perhaps Next itself where pushing to the router (and triggering GIP) causes the url params to appear as a path part in router.query['search-path'] i.e ['property', 'london', '?q=london']
    const validSearchPath = searchPath.filter(
        (v) => !v.startsWith('?') && v !== '_static' && v !== 'v1' && v !== 'v2'
    );

    return [searchPaths[propertyType], ...validSearchPath.slice(1)];
};

type ToRentFiltersProps = {
    onClose: () => void;
    onUpdate: () => void;
};

export const ToRentFilters = ({ onClose, onUpdate }: ToRentFiltersProps) => {
    const { query, push, pathname: currentPageRouteFilePath } = useRouter();

    const [isLoading, setIsLoading] = useState(false);

    const [accordionValue, updateAccordionValue] = useState<string[]>([]);

    const [formValue, updateFormValue] = useState(getToRentFilters(query));

    const onSubmit = async (e: { preventDefault: () => void }) => {
        setIsLoading(true);

        e.preventDefault();

        const updatedQuery = {
            'search-path': '', // keep typescript happy
            ...query,
            ...formValue,
            pn: ''
        };

        let updatedSearchPath = getSearchPath(
            formValue['property_type'] as PropertyType,
            query['search-path'] ?? ''
        );

        if (
            formValue['is_retirement_home'] === 'true' &&
            formValue['property_type'] === 'property'
        ) {
            delete (updatedQuery as Partial<typeof updatedQuery>)[
                'is_retirement_home'
            ];

            updatedSearchPath = getSearchPath('retirement', updatedSearchPath);
        }

        if (
            formValue['property_sub_type'].length === 1 &&
            formValue['property_sub_type'][0] === 'bungalow'
        ) {
            updatedSearchPath = getSearchPath('bungalow', updatedSearchPath);
        }

        if (formValue['property_type'] !== 'farms_land') {
            delete (updatedQuery as Partial<typeof updatedQuery>)[
                'property_type'
            ];
        }

        Object.keys(updatedQuery).forEach((key) => {
            if (updatedQuery[key as keyof typeof updatedQuery] === '') {
                delete updatedQuery[key as keyof typeof updatedQuery];
            }
        });

        // 'search-path' is the name of the dynamic route parameter and so we should not apply as a genuine query param below
        const { 'search-path': invalid, ...validParams } = updatedQuery;

        const pathname = currentPageRouteFilePath.includes('/map/')
            ? ['to-rent', 'map', ...updatedSearchPath]
            : ['to-rent', ...updatedSearchPath];

        const updatedPathname = `/${pathname.join('/')}/`;

        await push({
            pathname: updatedPathname,
            query: {
                ...validParams
            }
        });

        setIsLoading(false);

        onUpdate();
    };
    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            onSubmit({ preventDefault: () => event.preventDefault() });
        }
    };
    const panels = [
        {
            label: 'Bedrooms',
            value: 'bedrooms',
            component: (
                <Bedrooms
                    value={{
                        bedsMin: formValue.beds_min as BedsMin,
                        bedsMax: formValue.beds_max as BedsMax
                    }}
                    onChange={function ({
                        bedsMin,
                        bedsMax
                    }: {
                        bedsMin: string;
                        bedsMax: string;
                    }): void {
                        trackFilterClick();
                        updateFormValue((oldFormValue) => ({
                            ...oldFormValue,
                            beds_min: bedsMin,
                            beds_max: bedsMax
                        }));
                    }}
                />
            )
        },
        {
            label: 'Price',
            value: 'price',
            component: (
                <Price
                    value={{
                        priceMin: formValue.price_min as PriceMin,
                        priceMax: formValue.price_max as PriceMax,
                        priceFrequency: (formValue.price_frequency ||
                            'per_month') as PriceFrequency
                    }}
                    onChange={({ priceMin, priceMax, priceFrequency }) => {
                        trackFilterClick();
                        updateFormValue((oldFormValue) => ({
                            ...oldFormValue,
                            price_min: priceMin,
                            price_max: priceMax,
                            price_frequency: priceFrequency
                        }));
                    }}
                />
            )
        },
        {
            label: 'Property type',
            value: 'property_type',
            component: (
                <PropertyType
                    value={{
                        propertyType: formValue.property_type as PropertyType,
                        propertySubType:
                            formValue.property_sub_type as PropertySubType
                    }}
                    onChange={({ propertyType, propertySubType }) => {
                        trackFilterClick();
                        updateFormValue((oldFormValue) => ({
                            ...oldFormValue,
                            property_type: propertyType,
                            property_sub_type: propertySubType
                        }));
                    }}
                />
            )
        },
        {
            label: 'Include, exclude & show only',
            value: 'letting_type_variant',
            component: (
                <LettingType
                    value={{
                        isRetirementHome:
                            formValue.is_retirement_home as IsRetirementHome,
                        isSharedAccommodation:
                            formValue.is_shared_accommodation as IsSharedAccommodation
                    }}
                    onChange={({ isRetirementHome, isSharedAccommodation }) => {
                        trackFilterClick();
                        updateFormValue((oldFormValue) => ({
                            ...oldFormValue,
                            is_retirement_home: isRetirementHome,
                            is_shared_accommodation: isSharedAccommodation
                        }));
                    }}
                />
            )
        },
        {
            label: 'Must-haves',
            value: 'must_haves',
            component: (
                <MustHaves
                    value={{
                        billsIncluded:
                            formValue.bills_included as BillsIncluded,
                        petsAllowed: formValue.pets_allowed as PetsAllowed,
                        feature: formValue.feature as Features
                    }}
                    onChange={({ billsIncluded, petsAllowed, feature }) => {
                        trackFilterClick();
                        updateFormValue((oldFormValue) => ({
                            ...oldFormValue,
                            bills_included: billsIncluded,
                            pets_allowed: petsAllowed,
                            feature: feature
                        }));
                    }}
                />
            )
        },
        {
            label: 'Furnished',
            value: 'furnished',
            component: (
                <Furnishing
                    value={formValue.furnished_state as FurnishedState}
                    onChange={(furnishedState: FurnishedState) => {
                        trackFilterClick();
                        updateFormValue((oldFormValue) => ({
                            ...oldFormValue,
                            furnished_state: furnishedState
                        }));
                    }}
                />
            )
        },
        {
            label: 'Availability',
            value: 'available_from',
            component: (
                <Availability
                    value={formValue.available_from as AvailableFrom}
                    onChange={(availableFrom: AvailableFrom) => {
                        trackFilterClick();
                        updateFormValue((oldFormValue) => ({
                            ...oldFormValue,
                            available_from: availableFrom
                        }));
                    }}
                />
            )
        },
        {
            label: 'Added to site',
            value: 'added',
            component: (
                <AddedToSite
                    value={formValue.added as Added}
                    onChange={(added: Added) => {
                        trackFilterClick();
                        updateFormValue((oldFormValue) => ({
                            ...oldFormValue,
                            added: added
                        }));
                    }}
                />
            )
        }
    ];

    return (
        <Box display="flex" flexDirection="column" height="100%">
            <Box display="flex" justifyContent="center">
                <Button
                    category="tertiary"
                    onClick={() => {
                        trackFilterClick();
                        updateFormValue(getDefaultToRentFilters());
                    }}
                >
                    Reset
                </Button>
            </Box>
            <Divider direction="horizontal" />
            <div className={accordionAndKeywordsStyle}>
                <div className={accordionContainerStyle}>
                    <Accordion
                        value={accordionValue}
                        panels={panels}
                        onValueChange={updateAccordionValue}
                    />
                </div>
                <Box px="S400" bpM100={{ px: 'L400' }}>
                    <Keywords
                        value={formValue.keywords as Keywords}
                        onChange={(keywords) => {
                            trackFilterClick();
                            updateFormValue((oldFormValue) => ({
                                ...oldFormValue,
                                keywords: keywords
                            }));
                        }}
                        onKeyDown={handleKeyDown}
                    />
                </Box>
                <Box px="S300" bpM100={{ px: 'L300' }}>
                    <Divider direction="horizontal" />
                </Box>
                <Box px="S400" pb="L100" bpM100={{ px: 'L400' }}>
                    <LetAgreed
                        on={formValue['include_rented'] === 'true'}
                        onCheckedChange={(include_rented) => {
                            trackFilterClick();
                            updateFormValue((oldFormValue) => ({
                                ...oldFormValue,
                                include_rented
                            }));
                        }}
                    />
                </Box>
            </div>
            <div className={buttonWrapperStyle}>
                <div className={buttonContainerStyle}>
                    <Button
                        disabled={isLoading}
                        category="primary"
                        onClick={onSubmit}
                    >
                        Update Results
                    </Button>
                </div>
            </div>
        </Box>
    );
};
